import { Bookmark, BookmarkBorder, QrCode2 } from "@mui/icons-material";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import Grid from "@mui/material/GridLegacy";
import {
  GridActionsCellItem,
  GridRenderCellParams,
  GridRowId,
  GridRowModel,
  GridRowsProp,
} from "@mui/x-data-grid-pro";
import AddButton from "components/Buttons/AddButton";
import { CustomToolbar } from "components/MuiDataGrid/CustomToolbar";
import TransitionalRegion from "components/TransitionalRegion";
import VidletDataGrid from "components/VidletDataGrid";
import ROLE from "enums/ROLE_NAME";
import SCRIPT_TYPE from "enums/SCRIPT_TYPE";
import TEST_ID from "enums/TEST_ID";
import ScriptQrDialog from "features/ParticipantSignup/ScriptQrDialog";
import useGetScripts from "hooks/api/useGetScripts";
import useNavigateToScripts from "hooks/navigation/useNavigateToScripts";
import useCurrentUser from "hooks/useCurrentUser";
import useProject from "hooks/useProject";
import usePersistStore from "hooks/useStore/usePersistStore";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { flexCentered } from "theme/base/flex";
import ScriptDto from "types/ScriptDto";
import { getDataTestProp } from "utils/cypressTesting";
import { getPluralized } from "utils/display";
import getScriptColor from "utils/getScriptColor";
import { hasRole } from "utils/roles";
import ViewDetails from "../ScriptDetails";
import useScriptManagementStore from "../hooks/useScriptManagementStore";
import ScriptStats from "./ScriptStats";

export default function ScriptListing() {
  const { combinedPageResults: scripts, isLoading } = useGetScripts();
  const { activeId: scriptId } = useParams() as {
    activeId: string | undefined;
  };

  const { handleFormReset } = useScriptManagementStore();
  const { bookmarkedScripts, addBookmarkedScript, removeBookmarkedScript } =
    usePersistStore();
  const navigateToScripts = useNavigateToScripts();
  const [pinnedRowIds, setPinnedRowIds] = useState<{ top: GridRowId[] }>({
    top: [],
  });
  const { projectId } = useProject();
  const { role } = useCurrentUser();
  const canShowSignupQr = hasRole(ROLE.SUPER_DUPER_ADMIN, role);
  const [qrDialog, setQrDialog] = useState<{
    scriptId: string;
    title: string;
  } | null>(null);

  function isModerated(type: SCRIPT_TYPE) {
    return type === SCRIPT_TYPE.MODERATED || type === SCRIPT_TYPE.FOCUS_GROUP;
  }

  const scriptsData: GridRowsProp = useMemo(
    () =>
      scripts.map((script: ScriptDto) => ({
        id: script.id,
        title: script.title,
        description: script.description,
        status: script.status,
        createdOn: script.createdOn,
        startDate: isModerated(script.type) ? 0 : script.startDate,
        endDate: isModerated(script.type) ? 0 : script.endDate,
        type: script.type,
        qrSignupEligible: script.qrSignupEligible === true,
      })),
    [scripts],
  );

  const { rows, pinnedRows } = useMemo(() => {
    const rowsData: GridRowModel[] = [];
    const pinnedRowsData: { top: GridRowModel[] } = {
      top: [],
    };

    scriptsData.forEach((row) => {
      if (pinnedRowIds.top.includes(row.id) || bookmarkedScripts[row.id]) {
        pinnedRowsData.top.push(row);
      } else {
        rowsData.push(row);
      }
    });

    return {
      rows: rowsData,
      pinnedRows: pinnedRowsData,
    };
  }, [pinnedRowIds, scripts]);

  function Toolbar() {
    return (
      <CustomToolbar>
        <Stack sx={{ alignItems: "center", pr: 2 }} spacing={0.75}>
          <AddButton
            onClick={() => {
              handleFormReset();
              navigateToScripts("/add");
            }}
          >
            ADD NEW SCRIPT
          </AddButton>
          <Typography>
            {getPluralized("Script", scripts?.length || 0)}
          </Typography>
        </Stack>
      </CustomToolbar>
    );
  }

  const showScriptDetails = scriptId != undefined;
  return (
    <TransitionalRegion showSpinner={isLoading}>
      <Grid
        container
        sx={{ display: "flex", overflow: "hidden", height: "100vh", pb: 2 }}
      >
        <Grid
          item
          sx={{ height: "100%" }}
          md={showScriptDetails ? 5 : 12}
          display={
            showScriptDetails
              ? { xs: "none", sm: "none", md: "flex", lg: "flex" }
              : undefined
          }
        >
          <VidletDataGrid
            getRowClassName={(params) => {
              if (
                Object.keys(bookmarkedScripts).includes(params.id.toString())
              ) {
                return "Mui-selected";
              }
              return params.indexRelativeToCurrentPage % 2 === 0
                ? "Mui-even"
                : "Mui-odd";
            }}
            sx={{
              "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                py: "8px",
              },
              "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                py: "15px",
              },
              "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                py: "22px",
              },
            }}
            initialState={{
              sorting: {
                sortModel: [{ field: "createdOn", sort: "desc" }],
              },
            }}
            disableMultipleRowSelection
            rows={rows}
            columns={[
              {
                field: "actions",
                type: "actions",
                width: 130,
                getActions: (params) => {
                  const isPinnedTop =
                    pinnedRowIds.top.includes(params.id) ||
                    bookmarkedScripts[params.id];
                  const qrAction =
                    canShowSignupQr && params.row.qrSignupEligible ? (
                      <GridActionsCellItem
                        icon={
                          <Tooltip title="Signup QR code">
                            <QrCode2
                              {...getDataTestProp(
                                TEST_ID.participantSignup.qrAction,
                                String(params.id),
                              )}
                            />
                          </Tooltip>
                        }
                        label="Signup QR code"
                        onClick={() =>
                          setQrDialog({
                            scriptId: String(params.id),
                            title: params.row.title as string,
                          })
                        }
                      />
                    ) : null;
                  if (isPinnedTop) {
                    return [
                      <GridActionsCellItem
                        label="Remove Bookmark"
                        icon={
                          <Tooltip title="Remove Bookmark">
                            {isPinnedTop && <Bookmark />}
                          </Tooltip>
                        }
                        onClick={() => {
                          removeBookmarkedScript(params.id);
                          setPinnedRowIds((prevPinnedRowsIds) => ({
                            top: prevPinnedRowsIds.top.filter(
                              (rowId) => rowId !== params.id,
                            ),
                          }));
                        }}
                      />,
                      ...(qrAction ? [qrAction] : []),
                    ];
                  }
                  return [
                    <GridActionsCellItem
                      icon={
                        <Tooltip title="Bookmark Script">
                          <BookmarkBorder />
                        </Tooltip>
                      }
                      label="Bookmark Script"
                      onClick={() => {
                        addBookmarkedScript(params.id);
                        setPinnedRowIds((prevPinnedRowsIds) => ({
                          ...prevPinnedRowsIds,
                          top: [...prevPinnedRowsIds.top, params.id],
                        }));
                      }}
                    />,
                    ...(qrAction ? [qrAction] : []),
                  ];
                },
              },
              {
                field: "title",
                headerName: "Title",
                width: 220,
              },
              {
                field: "description",
                headerName: "Description",
                width: 300,
              },
              {
                field: "status",
                headerName: "Status",
                width: 320,
                renderCell: (params: GridRenderCellParams) => {
                  const status = params.row.status;
                  const type = params.row.type;
                  return (
                    <Stack direction="row" spacing={2}>
                      <Box
                        sx={{
                          ...flexCentered,
                          backgroundColor: (theme) =>
                            getScriptColor(type, status, {
                              moderatedColor:
                                theme.palette.script.moderatedColor,
                              focusGroupColor:
                                theme.palette.script.focusGroupColor,
                              publishedColor:
                                theme.palette.script.mobilePublishedColor,
                              draftColor: theme.palette.script.mobileDraftColor,
                              invitedColor: theme.palette.script.invitedColor,
                              inprogressColor:
                                theme.palette.script.inprogressColor,
                              completedColor:
                                theme.palette.script.completedColor,
                              paidColor: theme.palette.script.paidColor,
                              defaultColor: theme.palette.common.black,
                            }),
                        }}
                      >
                        <Typography
                          sx={({ palette, functions }) => ({
                            p: functions.pxToRem(10),
                            textAlign: "center",
                            width: functions.pxToRem(100),
                            color: palette.common.white,
                          })}
                        >
                          {isModerated(type) ? type : status}
                        </Typography>
                      </Box>
                      <ScriptStats scriptId={params.row.id} />
                    </Stack>
                  );
                },
                valueGetter: (params: GridRenderCellParams) => {
                  const status = params.row.status;
                  const type = params.row.type;
                  return isModerated(type) ? type : status;
                },
              },
              {
                field: "createdOn",
                headerName: "Created",
                width: 100,
                renderCell: (params) => {
                  return DateTime.fromMillis(
                    params.row.createdOn,
                  ).toLocaleString(DateTime.DATE_SHORT);
                },
              },
              {
                field: "startDate",
                headerName: "Start",
                width: 100,
                renderCell: (params) => {
                  if (isModerated(params.row.type)) return "";
                  return DateTime.fromSQL(params.row.startDate).toLocaleString(
                    DateTime.DATE_SHORT,
                  );
                },
              },
              {
                field: "endDate",
                headerName: "End",
                width: 100,
                renderCell: (params) => {
                  if (isModerated(params.row.type)) return "";
                  return DateTime.fromSQL(params.row.endDate).toLocaleString(
                    DateTime.DATE_SHORT,
                  );
                },
              },
            ]}
            pinnedRows={pinnedRows}
            slots={{ toolbar: Toolbar }}
            slotProps={{
              pagination: {
                SelectProps: {
                  sx: { width: "auto" },
                },
              },
              filterPanel: {
                sx: {
                  "& .MuiDataGrid-filterForm": {
                    alignItems: "flex-end",
                  },
                },
              },
            }}
            onRowClick={(params) => {
              navigateToScripts(`/${params.row.id}`);
            }}
          />
        </Grid>
        <Grid item xs={12} md={7} sx={{ height: "100%" }}>
          {showScriptDetails && <ViewDetails />}
        </Grid>
      </Grid>
      {canShowSignupQr && qrDialog && (
        <ScriptQrDialog
          open
          onClose={() => setQrDialog(null)}
          projectId={projectId}
          scriptId={qrDialog.scriptId}
          scriptTitle={qrDialog.title}
        />
      )}
    </TransitionalRegion>
  );
}
